import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DEFAULT_PERSISTED_QUOTE_EDITOR_STATE, normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import {
  getDatabaseCustomerName,
  getDatabaseProjectName,
  getQuoteDisplayName,
  getQuoteFileStem,
  getQuoteListPresentation,
  sanitizeFilenamePart,
} from '@shared/quote-naming';
import { computeQuotePreviewGross } from '@shared/quote-preview-totals';
import { useLocation } from 'wouter';
import { useOfferStore } from '../lib/store';
import { useIsMobile } from '../hooks/use-mobile';
import {
  type Database,
  type DatabaseMetadata,
  type ExportRecord,
  type QuoteSummary,
} from '../../../shared/types';
import Header from '../components/header';
import MobileApp from '../components/mobile-app';
import MobileErrorBoundary from '../components/mobile-error-boundary';
import MobileQuoteDesktopNotice, { type MobileQuoteGateStatus } from '../components/mobile-quote-desktop-notice';
import SimpleTable from '../components/simple-table';
import { BlitzkalkulationView } from '../components/kalkulation/blitzkalkulation-view';
import { AufmassView } from '../components/aufmass/aufmass-view';
import SimplePagination from '../components/simple-pagination';
import QuoteSidebar from '../components/quote-sidebar';
import LivePDFPreview from '../components/live-pdf-preview';
import { PDFViewerModal } from '../components/pdf-viewer-modal';
import GAEBEditor from '../components/gaeb/GAEBEditor';
import PDFImport from '../components/pdf-import';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Database as DatabaseIcon,
  FileText,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  FileSpreadsheet,
  Wand2,
  Trash2,
  Calendar,
  LayoutGrid,
  List,
  FolderOpen,
  PenLine,
  MoreVertical,
  Download,
  Pencil,
  FileJson,
  FileCode,
  Boxes,
  Building2,
  ArrowRight,
  Edit,
  ArrowUpDown,
  SlidersHorizontal,
  Filter,
  FileOutput,
  Eye,
  Printer,
  Copy,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { databaseService } from '../lib/database-service';
import { isRequestSupersededByUnload } from '../lib/page-lifecycle';
import {
  buildSnapshotReviewUrl,
  buildQuoteEditorUrl,
  chooseInitialDashboardTab,
  hasRequestedQuoteParam,
  parseRequestedSnapshotId,
  parseRequestedQuoteSlot,
  type DashboardTab,
} from '../lib/dashboard-navigation';
import {
  isQuoteSnapshot,
  resolveDatabaseOpenTarget,
  type DatabaseOpenTarget,
} from '../lib/quote-snapshot-routing';
import {
  deleteQuoteDataBySlot,
  duplicateQuoteDataBySlot,
  getStoredQuoteDataList,
  saveQuoteData,
} from '../lib/quote-data-service';
import { buildQuoteSaveRequest } from '../lib/quote-save-coordinator';
import { requestNextQuoteNumber, requestNachtragNumber, fetchNachtragBase } from '../lib/quote-number';
import {
  parseSoftecRuecksprung,
  resolveOpenQuoteNumber,
  type RuecksprungTreffer,
} from '../lib/softec-ruecksprung';
import { formatiereSumme } from '../lib/nachtrag-base-selection';
import { compareOfferNumbers, findDuplicateOfferNumbers } from '@shared/quote-number-circle';
import {
  createQuoteItemsFromDatabaseEntries,
  createRegularEntriesFromGaeb,
  enrichEntriesWithGaebQuantities,
  extractGaebSourceName,
} from '../lib/gaeb-offer-adapter';
import { useToast } from '../hooks/use-toast';

export default function Home() {
  const isDataLoaded = useOfferStore(s => s.isDataLoaded);
  const isGAEBDatabase = useOfferStore(s => s.isGAEBDatabase);
  const kalkulationsView = useOfferStore(s => s.kalkulationsView);
  const aufmassView = useOfferStore(s => s.aufmassView);
  const clearAll = useOfferStore(s => s.clearAll);
  const initNewQuote = useOfferStore(s => s.initNewQuote);
  const loadDatabaseData = useOfferStore(s => s.loadDatabaseData);
  const loadQuoteData = useOfferStore(s => s.loadQuoteData);
  const quoteItems = useOfferStore(s => s.quoteItems);
  const positions = useOfferStore(s => s.positions);
  const currentQuoteSlot = useOfferStore(s => s.currentQuoteSlot);
  const currentQuoteStorageRevision = useOfferStore(s => s.currentQuoteStorageRevision);
  const setQuoteHeader = useOfferStore(s => s.setQuoteHeader);
  const setQuoteItems = useOfferStore(s => s.setQuoteItems);
  
  const isMobile = useIsMobile();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [requestedQuote] = useState(() => ({
    hasParam: hasRequestedQuoteParam(window.location.search),
    slot: parseRequestedQuoteSlot(window.location.search),
  }));
  const requestedQuoteSlot = requestedQuote.slot;
  const [mobileQuoteGateStatus, setMobileQuoteGateStatus] = useState<MobileQuoteGateStatus | null>(
    requestedQuote.hasParam
      ? requestedQuoteSlot === null ? 'error' : 'loading'
      : null,
  );
  
  // Unified state
  const [databases, setDatabases] = useState<DatabaseMetadata[]>([]);
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [exports, setExports] = useState<ExportRecord[]>([]);

  // Mehrfach vergebene Angebotsnummern sichtbar machen (Bestand wird nicht
  // umgeschrieben — Olli soll die Doppelungen sehen und einmal aufräumen können).
  const duplicateOfferNumbers = useMemo(
    () => findDuplicateOfferNumbers(quotes.map((q) => q.offerNumber)),
    [quotes],
  );

  // Brücken-Vertrag aus der PM-App: /?source=softec&mode=nachtrag&softecProjectId=<ID>
  // → neues Angebot als Nachtrag vorbereiten. Unbekannte/fehlende Parameter:
  // heutiges Verhalten (Effect steigt aus, kein Absturz).
  const nachtragBridgeHandledRef = useRef(false);
  useEffect(() => {
    if (nachtragBridgeHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') !== 'softec' || params.get('mode') !== 'nachtrag') return;
    nachtragBridgeHandledRef.current = true;

    const softecProjectId = params.get('softecProjectId')?.trim();
    if (!softecProjectId) return;

    // projectId zusätzlich in die URL legen: Rechnungserstellung (quote-sidebar)
    // und Softec-Export lesen diesen bestehenden Parameter bereits — damit ist
    // das Zielprojekt für den späteren Export vorbelegt.
    params.set('projectId', softecProjectId);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);

    // Neues Angebot ohne Kreis-Nummer starten — die Nummer wird gleich als
    // Nachtragsnummer <Basis>/N<n> vergeben.
    initNewQuote({ allocateNumber: false });
    const epoch = useOfferStore.getState().offerDocumentEpoch;

    void (async () => {
      const base = await fetchNachtragBase(softecProjectId);
      if (useOfferStore.getState().offerDocumentEpoch !== epoch) return;

      if (base?.offerNumber) {
        const nummer = await requestNachtragNumber(base.offerNumber);
        const store = useOfferStore.getState();
        if (store.offerDocumentEpoch !== epoch) return;
        if (nummer && !(store.quoteHeader.angebot_nr || '').trim()) {
          store.updateQuoteHeader('angebot_nr', nummer);
          store.updateQuoteHeader('nachtragZu', base.offerNumber);
          toast({
            title: `Nachtrag ${nummer}`,
            description: `Neues Angebot als Nachtrag zu ${base.offerNumber} vorbereitet.`,
          });
          return;
        }
      }

      // Basisangebot nicht ermittelbar: normale Kreis-Nummer vergeben und den
      // Nutzer die Basis von Hand wählen lassen (Angebotsdaten-Dialog).
      const fallback = await requestNextQuoteNumber();
      const store = useOfferStore.getState();
      if (store.offerDocumentEpoch !== epoch) return;
      if (fallback && !(store.quoteHeader.angebot_nr || '').trim()) {
        store.updateQuoteHeader('angebot_nr', fallback);
      }
      toast({
        title: 'Nachtrag vorbereitet',
        description: 'Kein Basisangebot zum Projekt gefunden — bitte unter "Angebotsdaten" als Nachtrag zuordnen.',
      });
    })();
  }, [initNewQuote, toast]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<DashboardTab>('databases');
  const dashboardTabInitializedRef = useRef(false);
  const dashboardTabManuallySelectedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Sorting and display options (persisted in localStorage)
  const [sortBy, setSortBy] = useState<'created-desc' | 'created-asc' | 'updated-desc' | 'updated-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'number-asc' | 'number-desc'>(() => {
    const saved = localStorage.getItem('dashboard-sortBy');
    return (saved as any) || 'created-desc';
  });
  const [displayLimit, setDisplayLimit] = useState<number>(() => {
    const saved = localStorage.getItem('dashboard-displayLimit');
    return saved ? Number(saved) : 25;
  });
  const [typeFilter, setTypeFilter] = useState<string>(() => {
    return localStorage.getItem('dashboard-typeFilter') || 'all';
  });

  // Persist filter choices to localStorage
  useEffect(() => {
    localStorage.setItem('dashboard-sortBy', sortBy);
  }, [sortBy]);
  useEffect(() => {
    localStorage.setItem('dashboard-displayLimit', String(displayLimit));
  }, [displayLimit]);
  useEffect(() => {
    localStorage.setItem('dashboard-typeFilter', typeFilter);
  }, [typeFilter]);

  // App settings (company name, logo, colors)
  const [appSettings, setAppSettings] = useState<{
    companyName?: string;
    logoUrl?: string;
    primaryColor?: string;
  }>({});

  // Rename dialog state
  const [renameDialog, setRenameDialog] = useState<{
    isOpen: boolean;
    type: 'database' | 'quote';
    id: string | number;
    currentName: string;
  } | null>(null);
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Exit confirmation dialog state
  const [exitDialog, setExitDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Confirmation dialog state (replaces native confirm())
  const [deleteQuoteTarget, setDeleteQuoteTarget] = useState<{ slot: number } | null>(null);
  const [deleteDatabaseTarget, setDeleteDatabaseTarget] = useState<{ id: string } | null>(null);
  const [deleteExportTarget, setDeleteExportTarget] = useState<string | null>(null);

  // Database action dialog state (choose between edit DB or create offer)
  const [dbActionDialog, setDbActionDialog] = useState<{
    database: DatabaseMetadata;
    open: boolean;
  } | null>(null);
  const [snapshotReviewDialog, setSnapshotReviewDialog] = useState<{
    database: DatabaseMetadata;
    reason: Extract<DatabaseOpenTarget, { kind: 'readonly-snapshot' }>['reason'];
  } | null>(null);
  const [convertingSnapshotId, setConvertingSnapshotId] = useState<string | null>(null);
  const databaseUrlHandledRef = useRef(false);

  // Document preview modal state
  const [documentPreview, setDocumentPreview] = useState<{
    isOpen: boolean;
    pdfBlob: Blob | null;
    fileName: string;
    metadata: {
      customerName?: string;
      offerNumber?: string;
      totalGross?: number;
      itemCount?: number;
      createdAt?: string;
    };
    isLoading: boolean;
  }>({
    isOpen: false,
    pdfBlob: null,
    fileName: '',
    metadata: {},
    isLoading: false
  });

  // Load app settings (company name, logo, colors)
  useEffect(() => {
    fetch('/api/pdf-settings')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data) {
          setAppSettings({
            companyName: data.data.companyName,
            logoUrl: data.data.logoFilename ? '/api/pdf-settings/logo' : undefined,
            primaryColor: data.data.primaryColor
          });
        }
      })
      .catch((error) => {
        // Beim Entladen des Dokuments abgeräumt → kein Fehlschlag der App.
        if (isRequestSupersededByUnload(error)) return;
        console.error(error);
      });
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dbResult, quoteResult, exportsResult] = await Promise.all([
        databaseService.listDatabases({ status: 'active' }),
        fetch('/api/quote-data').then(r => r.json()),
        fetch('/api/exports').then(r => r.json()).catch(() => ({ success: false, exports: [] }))
      ]);

      // Deduplicate databases by ID to prevent display bugs
      const uniqueDatabases = dbResult.filter((db, index, self) =>
        index === self.findIndex(d => d.id === db.id)
      );
      const loadedQuotes = quoteResult.success && quoteResult.data?.files
        ? quoteResult.data.files
        : null;
      const loadedExports = exportsResult.success && exportsResult.exports
        ? exportsResult.exports
        : null;

      setDatabases(uniqueDatabases);
      if (loadedQuotes !== null) {
        setQuotes(loadedQuotes);
      }
      if (loadedExports !== null) {
        setExports(loadedExports);
      }

      const dashboardCountsKnown = loadedQuotes !== null && loadedExports !== null;
      if (!dashboardTabInitializedRef.current && dashboardCountsKnown) {
        setActiveTab((preferredTab) => chooseInitialDashboardTab(
          preferredTab,
          {
            databases: uniqueDatabases.filter((database) =>
              !isQuoteSnapshot(database)
            ).length,
            quotes: loadedQuotes.length,
            outputs: loadedExports.length,
          },
          dashboardTabManuallySelectedRef.current,
        ));
        dashboardTabInitializedRef.current = true;
      }
    } catch (error) {
      // Ein Request, den die Navigation abgeräumt hat, ist kein Fehlschlag der
      // App. Echte Netzwerkfehler werden weiterhin geloggt.
      if (isRequestSupersededByUnload(error)) return;
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle canonical quote URL parameters.
  // CRITICAL: empty deps [] + getState() — having [isDataLoaded, loadQuoteData, loadDatabaseData]
  // caused React #185 infinite loop because deps changed on every set() inside loadQuoteData.
  useEffect(() => {
    // Defer to next tick so loadData's initial state updates complete first.
    // Without this, concurrent set() from loadData and loadQuoteData cause #185.
    const timer = setTimeout(() => {
      const loadQuoteSlot = parseRequestedQuoteSlot(window.location.search);

      if (loadQuoteSlot !== null) {
        useOfferStore.getState().loadQuoteData(loadQuoteSlot).then(() => {
          window.history.replaceState(
            {},
            '',
            buildQuoteEditorUrl(loadQuoteSlot, window.location.search),
          );
          setMobileQuoteGateStatus('loaded');
        }).catch((error) => {
          console.error(error);
          setMobileQuoteGateStatus('error');
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // URL sync is handled by loadQuoteData's .then() and handleBackClick.
  // The previous useEffect with [isDataLoaded, currentQuoteSlot, currentDatabaseId]
  // contributed to React #185 by re-firing during the set() render cycle.

  // ============================================================
  // Rück-Sprung aus Softec ("Angebot aus der Projektmaske öffnen"):
  //   /?source=softec&openQuoteSlot=<slot>   → direkt öffnen (Slot ist eindeutig)
  //   /?source=softec&openQuoteNumber=<nr>   → Bestands-Rückfall über die Nummer;
  //     1 Treffer → öffnen, mehrere → Auswahl (nie raten), keiner → Ansage.
  // Ladeweg ist der VORHANDENE (loadQuoteData + buildQuoteEditorUrl) — kein
  // zweiter Pfad. Parsen/Auflösen: lib/softec-ruecksprung.ts (pur, getestet).
  // ============================================================
  const ruecksprungHandledRef = useRef(false);
  const [ruecksprungAuswahl, setRuecksprungAuswahl] = useState<RuecksprungTreffer[] | null>(null);
  const [ruecksprungConfirm, setRuecksprungConfirm] = useState<{ slot: number } | null>(null);

  const ladeRuecksprungSlot = useCallback(async (slot: number) => {
    try {
      await useOfferStore.getState().loadQuoteData(slot);
      window.history.replaceState({}, '', buildQuoteEditorUrl(slot, window.location.search));
    } catch (error) {
      // Gelöschter/unbekannter Slot: verständliche Ansage statt leerem Editor.
      console.error(error);
      toast({
        title: 'Angebot nicht gefunden',
        description: `In Ablage #${slot} liegt kein Angebot (mehr) — möglicherweise wurde es gelöscht oder verschoben. Bitte im Reiter Entwürfe nachsehen.`,
        variant: 'destructive',
      });
    }
  }, [toast]);

  const oeffneRuecksprungZiel = useCallback((slot: number) => {
    const store = useOfferStore.getState();
    if (store.isDataLoaded && store.currentQuoteSlot === slot) {
      // Ziel ist bereits geöffnet — NICHT neu laden, sonst gingen offene
      // Änderungen verloren. Nur die Adresse kanonisieren.
      window.history.replaceState({}, '', buildQuoteEditorUrl(slot, window.location.search));
      return;
    }
    // Gleiche Definition wie hasUnsavedChanges/handleBackClick: sobald der
    // Editor Inhalt trägt, wird vor dem Überschreiben gefragt.
    const offeneAenderungen = store.quoteItems.length > 0 || store.positions.length > 0;
    if (offeneAenderungen) {
      setRuecksprungConfirm({ slot });
      return;
    }
    void ladeRuecksprungSlot(slot);
  }, [ladeRuecksprungSlot]);

  useEffect(() => {
    // Nummern-Auflösung braucht die geladene Angebotsliste → nach loadData.
    // Mobil bleibt der Rück-Sprung außen vor (der Editor ist Desktop-gegated).
    if (loading || isMobile || ruecksprungHandledRef.current) return;
    const request = parseSoftecRuecksprung(window.location.search);
    if (!request) return;
    ruecksprungHandledRef.current = true;

    if (request.kind === 'ungueltig') {
      toast({
        title: 'Rück-Sprung aus Softec nicht möglich',
        description: request.grund,
        variant: 'destructive',
      });
      return;
    }

    if (request.kind === 'slot') {
      oeffneRuecksprungZiel(request.slot);
      return;
    }

    const aufloesung = resolveOpenQuoteNumber(quotes, request.nummer);
    if (aufloesung.status === 'eindeutig') {
      oeffneRuecksprungZiel(aufloesung.treffer.slot);
    } else if (aufloesung.status === 'mehrdeutig') {
      setRuecksprungAuswahl(aufloesung.treffer);
    } else {
      toast({
        title: 'Kein Angebot gefunden',
        description: `Zur Angebotsnummer "${request.nummer}" wurde kein gespeichertes Angebot gefunden. Möglicherweise wurde es gelöscht oder umnummeriert.`,
        variant: 'destructive',
      });
    }
  }, [loading, isMobile, quotes, oeffneRuecksprungZiel, toast]);

  // Warn before closing tab/browser when data is loaded
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDataLoaded && quoteItems.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDataLoaded, quoteItems.length]);

  // Filter, sort and limit items
  const filteredDatabases = useMemo(() => {
    let result = [...databases];

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(db => db.type === typeFilter);
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(db =>
        db.name?.toLowerCase().includes(term) ||
        db.projectInfo?.description?.toLowerCase().includes(term) ||
        db.projectInfo?.customerName?.toLowerCase().includes(term) ||
        db.projectInfo?.location?.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'created-desc':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'created-asc':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'updated-desc':
          return new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime();
        case 'updated-asc':
          return new Date(a.updatedAt || a.createdAt || 0).getTime() - new Date(b.updatedAt || b.createdAt || 0).getTime();
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '', 'de');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '', 'de');
        case 'size-desc':
          return (b.itemCount || 0) - (a.itemCount || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [databases, searchTerm, sortBy, typeFilter]);

  const filteredQuotes = useMemo(() => {
    let result = [...quotes];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(q =>
        q.projectName?.toLowerCase().includes(term) ||
        q.customerName?.toLowerCase().includes(term) ||
        q.offerNumber?.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'created-desc':
        case 'updated-desc':
          return new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime();
        case 'created-asc':
        case 'updated-asc':
          return new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime();
        case 'name-asc':
          return getQuoteDisplayName(a, `Angebot ${a.slot}`).localeCompare(getQuoteDisplayName(b, `Angebot ${b.slot}`), 'de');
        case 'name-desc':
          return getQuoteDisplayName(b, `Angebot ${b.slot}`).localeCompare(getQuoteDisplayName(a, `Angebot ${a.slot}`), 'de');
        case 'size-desc':
          return (b.totalValue || 0) - (a.totalValue || 0);
        case 'number-asc':
          return compareOfferNumbers(a.offerNumber, b.offerNumber, 'asc');
        case 'number-desc':
          return compareOfferNumbers(a.offerNumber, b.offerNumber, 'desc');
        default:
          return 0;
      }
    });

    return result;
  }, [quotes, searchTerm, sortBy]);

  // Apply display limit
  const displayedDatabases = useMemo(() => {
    if (displayLimit === 0) return filteredDatabases;
    return filteredDatabases.slice(0, displayLimit);
  }, [filteredDatabases, displayLimit]);

  const displayedQuotes = useMemo(() => {
    if (displayLimit === 0) return filteredQuotes;
    return filteredQuotes.slice(0, displayLimit);
  }, [filteredQuotes, displayLimit]);

  // Handlers
  const handleDatabaseClick = (database: DatabaseMetadata) => {
    const target = resolveDatabaseOpenTarget(database, quotes);
    if (target.kind === 'quote') {
      void handleQuoteSelect(target.slot);
      return;
    }
    if (target.kind === 'readonly-snapshot') {
      window.history.replaceState({}, '', buildSnapshotReviewUrl(database.id));
      setSnapshotReviewDialog({ database, reason: target.reason });
      return;
    }

    setDbActionDialog({ database, open: true });
  };

  const handleEditDatabase = (database: DatabaseMetadata) => {
    setDbActionDialog(null);
    navigate(`/database-builder?edit=${database.id}`);
  };

  const handleConvertSnapshot = async (database: DatabaseMetadata) => {
    setConvertingSnapshotId(database.id);
    try {
      const snapshot = await databaseService.loadDatabase(database.id);
      const converted = databaseService.createEmptyDatabaseCopy(snapshot);
      if (converted.id === database.id || converted.type !== 'manual') {
        throw new Error('Snapshot-Konvertierung lieferte keine neue manuelle Datenbank');
      }

      const saved = await databaseService.saveDatabase(converted);
      if (
        saved.id !== converted.id
        || saved.id === database.id
        || saved.type !== 'manual'
      ) {
        throw new Error('Konvertierte Datenbank konnte nicht eindeutig validiert werden');
      }

      setSnapshotReviewDialog(null);
      navigate(`/database-builder?edit=${encodeURIComponent(saved.id)}`);
    } catch (error) {
      toast({
        title: 'Konvertierung fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setConvertingSnapshotId(null);
    }
  };

  const handleMobileSnapshotSelect = useCallback(async (
    database: DatabaseMetadata,
  ): Promise<void> => {
    const target = resolveDatabaseOpenTarget(database, quotes);
    if (target.kind === 'quote') {
      setMobileQuoteGateStatus('loading');
      try {
        await useOfferStore.getState().loadQuoteData(target.slot);
        window.history.replaceState(
          {},
          '',
          buildQuoteEditorUrl(target.slot, window.location.search),
        );
        setMobileQuoteGateStatus('loaded');
      } catch (error) {
        console.error(error);
        setMobileQuoteGateStatus('error');
      }
      return;
    }

    window.history.replaceState({}, '', buildSnapshotReviewUrl(database.id));
    setSnapshotReviewDialog({
      database,
      reason: target.kind === 'readonly-snapshot' ? target.reason : 'missing',
    });
  }, [quotes]);

  // GAEB-specific: Open GAEB editor to edit the GAEB file
  const handleEditGAEB = async (database: DatabaseMetadata) => {
    setDbActionDialog(null);
    try {
      await loadDatabaseData(database.id);
      toast({ title: "X83 geladen", description: database.name });
    } catch (error) {
      toast({ title: "Fehler", description: "X83 konnte nicht geladen werden", variant: "destructive" });
    }
  };

  // GAEB-specific: Create a regular database from GAEB entries
  const handleCreateDbFromGAEB = async (database: DatabaseMetadata) => {
    setDbActionDialog(null);
    try {
      // 1. Load the full GAEB database
      const gaebDatabase = await databaseService.loadDatabase(database.id);

      // 2. Convert GAEB entries to regular database entries (flatten hierarchy)
      const regularEntries = createRegularEntriesFromGaeb(gaebDatabase.entries);

      // 3. Create new database metadata
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];

      // 4. Prepare projectInfo, entries, and source for createDatabase
      const projectInfo = {
        date: dateStr,
        projectNumber: gaebDatabase.gaebMetadata?.projectName,
        customerName: gaebDatabase.gaebMetadata?.clientName,
        description: `Erstellt aus X83: ${database.name}`,
      };

      const source = {
        createdBy: 'manual' as const,
        originalFilename: database.name,
      };

      // 5. Save as new regular database
      const newDatabase = await databaseService.createDatabase(
        projectInfo,
        regularEntries,
        source
      );

      // 5. Refresh the database list
      await loadData();

      toast({
        title: "Datenbank erstellt",
        description: `${regularEntries.length} Positionen aus X83 übernommen`
      });
    } catch (error) {
      console.error('Error creating database from GAEB:', error);
      toast({
        title: "Fehler",
        description: "Datenbank konnte nicht erstellt werden",
        variant: "destructive"
      });
    }
  };

  const handleCreateOfferFromDb = async (database: DatabaseMetadata) => {
    setDbActionDialog(null);
    try {
      // 1. Load the full database with entries
      const fullDatabase = await databaseService.loadDatabase(database.id);
      const isGAEB = fullDatabase.type === 'gaeb-import';

      let entriesForQuote: any[] = fullDatabase.entries as any[];
      if (!isGAEB) {
        const sourceName = extractGaebSourceName(fullDatabase);
        const needsQuantityBackfill = sourceName && !fullDatabase.entries.some((entry: any) => {
          const quantity = entry.adjustedQuantity ?? entry.originalQuantity ?? entry.menge ?? 0;
          return Number(quantity) > 0;
        });

        if (needsQuantityBackfill) {
          const sourceDatabase = databases.find((candidate) =>
            candidate.type === 'gaeb-import' &&
            (candidate.name === sourceName || candidate.name.includes(sourceName))
          );
          if (sourceDatabase) {
            const sourceGaebDatabase = await databaseService.loadDatabase(sourceDatabase.id);
            entriesForQuote = enrichEntriesWithGaebQuantities(fullDatabase.entries, sourceGaebDatabase.entries);
          }
        }
      }

      // 2. Convert database entries to quote items.
      // GAEB-derived regular databases preserve their original quantities;
      // pure catalogue databases still start with menge=0 for manual offer building.
      const quoteItems = createQuoteItemsFromDatabaseEntries(entriesForQuote, { isGaebImport: isGAEB });
      const positions = quoteItems;

      // 4. Generate new Angebot header — Nummer fortlaufend aus dem
      // Nummernkreis (EINE gemeinsame Vergabestelle, kein Zufall mehr).
      // Server nicht erreichbar → leer lassen; das Sidebar-Auffangnetz bzw.
      // der Nutzer vergibt die Nummer dann später.
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const angebotNr = (await requestNextQuoteNumber()) ?? '';
      const newHeader = {
        kunde: getDatabaseCustomerName(database) || '',
        anschrift: '',
        angebot_nr: angebotNr,
        bauvorhaben: getDatabaseProjectName(database) || '',
        datum: dateStr,
      };

      // 5. Extract unique Gewerke for gewerkOrder
      const gewerkSet = new Set(positions.map((p: any) => p.gewerk).filter((g: string) => g));
      const gewerkOrder = Array.from(gewerkSet) as string[];

      // 6. Generate filename based on database name
      const filename = sanitizeFilenamePart(
        getQuoteFileStem({ header: newHeader }, 'Angebot'),
        `Angebot ${dateStr}`,
      );

      // 7. Save the new Angebot to server
      const savedQuote = await saveQuoteData({
        ...buildQuoteSaveRequest(
          {
            header: newHeader,
            items: quoteItems,
            positions,
            gewerkOrder: gewerkOrder as string[],
            editorState: DEFAULT_PERSISTED_QUOTE_EDITOR_STATE,
          },
          null,
          'save',
        ),
        filename,
      });

      // 8. Use the actual slot allocated by the save response.
      const savedSlot = savedQuote.metadata.slot;

      // 9. Refresh the quotes list
      await loadData();

      // 10. Load the saved Angebot in the editor by slot (if available)
      if (savedSlot) {
        await loadQuoteData(savedSlot);
        window.history.replaceState(
          {},
          '',
          buildQuoteEditorUrl(savedSlot, window.location.search),
        );
      } else {
        // Fallback: Load database and set quote data manually
        await loadDatabaseData(database.id);
        setQuoteHeader(newHeader);
        setQuoteItems(quoteItems);
      }

      toast({
        title: "Neues Angebot erstellt",
        description: newHeader.angebot_nr
          ? `${newHeader.angebot_nr} basierend auf "${database.name}"`
          : `Basierend auf "${database.name}" (Angebotsnummer wird nachgetragen)`
      });
    } catch (error) {
      console.error('Error creating offer from database:', error);
      toast({
        title: "Fehler",
        description: "Angebot konnte nicht erstellt werden",
        variant: "destructive"
      });
    }
  };

  const handleQuoteSelect = async (slot: number) => {
    try {
      await loadQuoteData(slot);
      window.history.replaceState(
        {},
        '',
        buildQuoteEditorUrl(slot, window.location.search),
      );
      const quote = quotes.find(q => q.slot === slot);
      toast({ title: "Angebot geladen", description: getQuoteDisplayName(quote || {}, `Slot ${slot}`) });
    } catch (error) {
      toast({ title: "Fehler", description: "Laden fehlgeschlagen", variant: "destructive" });
    }
  };

  const handleDeleteQuote = async (slot: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteQuoteTarget({ slot });
  };

  const confirmDeleteQuote = async () => {
    if (!deleteQuoteTarget) return;
    const { slot } = deleteQuoteTarget;
    setDeleteQuoteTarget(null);

    setDeletingId(slot);
    try {
      await deleteQuoteDataBySlot(slot);
      setQuotes(prev => prev.filter(q => q.slot !== slot));
      toast({ title: "Gelöscht" });
    } catch (error) {
      toast({ title: "Fehler", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  // Handle PDF preview for a quote from Dokumente tab
  const handleDocumentPreview = async (quote: QuoteSummary) => {
    setDocumentPreview(prev => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      fileName: `${sanitizeFilenamePart(getQuoteFileStem(quote, `Angebot ${quote.slot}`), `Angebot ${quote.slot}`)}.pdf`,
      metadata: {
        customerName: quote.customerName,
        offerNumber: quote.offerNumber,
        itemCount: quote.itemCount,
        createdAt: quote.timestamp
      }
    }));

    try {
      // Load quote data first
      const response = await fetch(`/api/quote-data/${quote.slot}`);
      if (!response.ok) throw new Error('Failed to load quote data');
      const result = await response.json();

      // API returns { success, data, error } structure
      if (!result.success || !result.data) {
        throw new Error(result.error || 'No quote data found');
      }

      const quoteData = result.data;

      // Check if we have the required data
      if (!quoteData.items || quoteData.items.length === 0) {
        throw new Error('Keine Positionen im Angebot');
      }

      const totalGross = computeQuotePreviewGross(
        quoteData.items,
        quoteData.header?.mwstSatz,
      );
      setDocumentPreview(prev => ({
        ...prev,
        metadata: { ...prev.metadata, totalGross },
      }));

      // Generate PDF using the accurate endpoint
      // Filter items with quantity > 0 (support both standard 'menge' and GAEB quantity fields)
      const itemsWithQuantity = quoteData.items.filter((i: any) => {
        const qty = i.menge ?? i.adjustedQuantity ?? i.originalQuantity ?? 0;
        return qty > 0;
      });

      const pdfResponse = await fetch('/api/generate-pdf-accurate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: quoteData.header || {},
          items: itemsWithQuantity,
          gewerkOrder: quoteData.gewerkOrder || [],
          options: { aiOptimization: false }
        })
      });

      if (!pdfResponse.ok) {
        const errorText = await pdfResponse.text();
        throw new Error(`PDF generation failed: ${errorText}`);
      }

      const pdfBlob = await pdfResponse.blob();

      setDocumentPreview(prev => ({
        ...prev,
        pdfBlob,
        isLoading: false
      }));
    } catch (error) {
      console.error('Document preview error:', error);
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "PDF-Vorschau konnte nicht generiert werden",
        variant: "destructive"
      });
      setDocumentPreview(prev => ({ ...prev, isOpen: false, isLoading: false }));
    }
  };

  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      pdfBlob: null,
      fileName: '',
      metadata: {},
      isLoading: false
    });
  };

  // Handle delete database
  const handleDeleteDatabase = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDatabaseTarget({ id });
  };

  const confirmDeleteDatabase = async () => {
    if (!deleteDatabaseTarget) return;
    const { id } = deleteDatabaseTarget;
    setDeleteDatabaseTarget(null);

    setDeletingId(id);
    try {
      await databaseService.deleteDatabase(id);
      setDatabases(prev => prev.filter(db => db.id !== id));
      toast({ title: "Datenbank gelöscht" });
    } catch (error) {
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  // Handle rename
  const handleRename = async () => {
    if (!renameDialog || !newName.trim()) return;

    setIsRenaming(true);
    try {
      if (renameDialog.type === 'database') {
        await databaseService.updateDatabase(renameDialog.id as string, { name: newName.trim() });
        setDatabases(prev =>
          prev.map(db => db.id === renameDialog.id ? { ...db, name: newName.trim() } : db)
        );
        toast({ title: "Datenbank umbenannt" });
      } else {
        // Update quote metadata
        const response = await fetch(`/api/quote-data/${renameDialog.id}/metadata`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectName: newName.trim() }),
        });
        if (!response.ok) throw new Error('Rename failed');
        setQuotes(prev =>
          prev.map(q => q.slot === renameDialog.id ? { ...q, projectName: newName.trim() } : q)
        );
        toast({ title: "Angebotstitel aktualisiert" });
      }
      setRenameDialog(null);
      setNewName('');
    } catch (error) {
      toast({ title: "Umbenennen fehlgeschlagen", variant: "destructive" });
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle download
  const handleDownload = async (type: 'database' | 'quote', id: string | number, format: 'json' | 'excel') => {
    try {
      let url: string;
      let filename: string;

      if (type === 'database') {
        const db = databases.find(d => d.id === id);
        filename = `${db?.name || 'database'}.${format === 'json' ? 'json' : 'xlsx'}`;
        url = `/api/databases/${id}/export?format=${format}`;
      } else {
        const quote = quotes.find(q => q.slot === id);
        const fileStem = sanitizeFilenamePart(
          getQuoteFileStem(quote || {}, `Angebot ${id}`),
          `Angebot ${id}`,
        );
        filename = `${fileStem}.${format === 'json' ? 'json' : 'xlsx'}`;
        url = `/api/quote-data/${id}/export?format=${format}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      toast({ title: "Download gestartet" });
    } catch (error) {
      toast({ title: "Download fehlgeschlagen", variant: "destructive" });
    }
  };

  // Handle duplicate database
  const handleDuplicateDatabase = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/databases/${id}/duplicate`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Duplizieren fehlgeschlagen');
      toast({ title: "Datenbank dupliziert" });
      loadData();
    } catch (error) {
      toast({ title: "Duplizieren fehlgeschlagen", variant: "destructive" });
    }
  };

  // Handle duplicate quote
  const handleDuplicateQuote = async (slot: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await duplicateQuoteDataBySlot(slot);
      const duplicatedOfferNumber = result.quote.header.angebot_nr;
      const duplicatedSlot = result.newSlot;
      toast({
        title: "Angebot dupliziert",
        description: duplicatedOfferNumber
          ? `${duplicatedOfferNumber}${duplicatedSlot ? ` (Slot #${duplicatedSlot})` : ''}`
          : undefined,
      });
      loadData();
    } catch (error) {
      toast({
        title: "Duplizieren fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive"
      });
    }
  };

  // Handle X83 download (original GAEB format)
  const handleX83Download = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const db = databases.find(d => d.id === id);
      const response = await fetch('/api/gaeb/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          databaseId: id,
          exportFormat: 'x83',
          includeComments: true
        })
      });

      if (!response.ok) throw new Error('X83 Export fehlgeschlagen');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName = db?.name?.replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_') || 'export';
      link.download = `${baseName}.x83`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({ title: "X83 Export gestartet" });
    } catch (error) {
      toast({ title: "X83 Export fehlgeschlagen", variant: "destructive" });
    }
  };

  const confirmDeleteExport = async () => {
    if (!deleteExportTarget) return;
    const exportId = deleteExportTarget;
    setDeleteExportTarget(null);

    try {
      await fetch(`/api/exports/${exportId}`, { method: 'DELETE' });
      setExports(prev => prev.filter(e => e.id !== exportId));
      toast({ title: "Export gelöscht" });
    } catch {
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  // Check if there are unsaved changes (items with quantities or GAEB positions)
  const hasUnsavedChanges = quoteItems.length > 0 || positions.length > 0;

  // Handle back button click
  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setExitDialog(true);
    } else {
      // No changes, just go back
      clearAll();
      navigate('/');
    }
  };

  // Speichert den aktuellen Editorstand in seinen Slot (bzw. neuen Slot) —
  // gemeinsame Grundlage für "Speichern & Zurück" und den Softec-Rück-Sprung.
  const saveCurrentQuote = async () => {
    const storeState = useOfferStore.getState();
    const { quoteHeader, positions: storePositions, gewerkOrder } = storeState;
    const editorState = normalizePersistedQuoteEditorState(storeState);
    const filename = getQuoteFileStem({ header: quoteHeader }, 'Entwurf');

    await saveQuoteData({
      ...buildQuoteSaveRequest(
        {
          header: quoteHeader,
          items: storeState.quoteItems,
          positions: storePositions,
          gewerkOrder: gewerkOrder as string[],
          editorState,
        },
        storeState.currentQuoteSlot === null
          ? null
          : {
              slot: storeState.currentQuoteSlot,
              storageRevision: storeState.currentQuoteStorageRevision ?? 0,
            },
        'save',
      ),
      filename,
    });
  };

  // Save current work and go back
  const handleSaveAndExit = async () => {
    setIsSaving(true);
    try {
      await saveCurrentQuote();

      toast({
        title: "Daten gespeichert",
        description: currentQuoteSlot
          ? `Angebot #${currentQuoteSlot} wurde gespeichert.`
          : "Angebot wurde gespeichert."
      });
      setExitDialog(false);
      clearAll();
      navigate('/');
    } catch (error) {
      toast({
        title: "Speichern fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes and go back
  const handleDiscardAndExit = () => {
    setExitDialog(false);
    clearAll();
    navigate('/');
  };

  // Rück-Sprung-Ziel öffnen, offenen Stand vorher sichern.
  const handleRuecksprungSaveAndOpen = async () => {
    if (!ruecksprungConfirm) return;
    const zielSlot = ruecksprungConfirm.slot;
    setIsSaving(true);
    try {
      await saveCurrentQuote();
      setRuecksprungConfirm(null);
      await ladeRuecksprungSlot(zielSlot);
    } catch (error) {
      // Speichern gescheitert → NICHTS überschreiben, Dialog bleibt offen.
      toast({
        title: 'Speichern fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Rück-Sprung-Ziel öffnen, offenen Stand bewusst verwerfen.
  const handleRuecksprungDiscardAndOpen = () => {
    if (!ruecksprungConfirm) return;
    const zielSlot = ruecksprungConfirm.slot;
    setRuecksprungConfirm(null);
    void ladeRuecksprungSlot(zielSlot);
  };

  const handleReturnFromMobileQuote = useCallback(() => {
    clearAll();
    setMobileQuoteGateStatus(null);
    window.history.replaceState({}, '', '/');
    navigate('/');
  }, [clearAll, navigate]);

  // Die Sortierung nach Angebotsnummer gibt es nur im Entwürfe-Tab. Auf anderen
  // Tabs (auch nach Neustart mit gespeicherter Auswahl) auf den Standard
  // zurückfallen — sonst zeigt das Sortier-Auswahlfeld eine leere Auswahl.
  useEffect(() => {
    if (activeTab !== 'quotes' && (sortBy === 'number-asc' || sortBy === 'number-desc')) {
      setSortBy('created-desc');
    }
  }, [activeTab, sortBy]);

  const handleDashboardTabChange = useCallback((value: string) => {
    dashboardTabManuallySelectedRef.current = true;
    dashboardTabInitializedRef.current = true;
    setActiveTab(value as DashboardTab);
  }, []);

  // Legacy ?db= and guarded ?snapshot= are classified only after both
  // physical databases and canonical quote summaries have loaded.
  useEffect(() => {
    if (loading || databaseUrlHandledRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const snapshotId = parseRequestedSnapshotId(window.location.search);
    const legacyDatabaseId = params.get('db')?.trim() || null;
    const requestedDatabaseId = snapshotId ?? legacyDatabaseId;
    if (!requestedDatabaseId) {
      return;
    }

    databaseUrlHandledRef.current = true;
    const database = databases.find((candidate) =>
      candidate.id === requestedDatabaseId
    );
    if (!database) {
      return;
    }

    if (isMobile && isQuoteSnapshot(database)) {
      void handleMobileSnapshotSelect(database);
      return;
    }

    const target = resolveDatabaseOpenTarget(database, quotes);
    if (target.kind === 'quote') {
      useOfferStore.getState().loadQuoteData(target.slot).then(() => {
        window.history.replaceState(
          {},
          '',
          buildQuoteEditorUrl(target.slot, window.location.search),
        );
      }).catch(console.error);
      return;
    }

    if (target.kind === 'readonly-snapshot') {
      window.history.replaceState({}, '', buildSnapshotReviewUrl(database.id));
      setSnapshotReviewDialog({ database, reason: target.reason });
      return;
    }

    if (legacyDatabaseId) {
      useOfferStore.getState().loadDatabaseData(database.id).catch(console.error);
    }
  }, [
    databases,
    handleMobileSnapshotSelect,
    isMobile,
    loading,
    quotes,
  ]);

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('de-DE', { 
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const snapshotReviewOverlay = (
    <Dialog
      open={snapshotReviewDialog !== null}
      onOpenChange={(open) => {
        if (!open) {
          setSnapshotReviewDialog(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Schreibgeschützter Angebots-Snapshot</DialogTitle>
          <DialogDescription>
            {snapshotReviewDialog?.reason === 'ambiguous'
              ? 'Mehrere Entwürfe haben dieselbe Angebotsnummer. Der historische Snapshot wird deshalb nicht automatisch zugeordnet.'
              : 'Zu diesem historischen Snapshot wurde kein eindeutiger Entwurf gefunden.'}
          </DialogDescription>
        </DialogHeader>
        {snapshotReviewDialog && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="font-medium text-amber-900">
                {snapshotReviewDialog.database.name}
              </p>
              <p className="text-sm text-amber-800">
                Der Snapshot bleibt unverändert und kann nicht bearbeitet werden.
              </p>
            </div>
            <Button
              className="w-full"
              data-testid={`button-convert-snapshot-${snapshotReviewDialog.database.id}`}
              disabled={convertingSnapshotId === snapshotReviewDialog.database.id}
              onClick={() => void handleConvertSnapshot(snapshotReviewDialog.database)}
            >
              {convertingSnapshotId === snapshotReviewDialog.database.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <DatabaseIcon className="mr-2 h-4 w-4" />
              )}
              In Katalog konvertieren
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSnapshotReviewDialog(null)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const ruecksprungOverlay = (
    <>
      {/* Mehrere Angebote mit derselben Nummer: Auswahl mit Nummer, Kunde,
          Bauvorhaben, Datum, Summe (Spalten wie der Nachtrags-Picker in
          header.tsx) — es wird NIE stillschweigend das erste geöffnet. */}
      <Dialog
        open={ruecksprungAuswahl !== null}
        onOpenChange={(open) => {
          if (!open) setRuecksprungAuswahl(null);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[75vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Mehrere Angebote mit dieser Nummer</DialogTitle>
            <DialogDescription>
              Die Angebotsnummer &bdquo;{ruecksprungAuswahl?.[0]?.offerNumber}&ldquo; ist{' '}
              {ruecksprungAuswahl?.length ?? 0}-mal vergeben. Bitte anhand von Kunde,
              Bauvorhaben, Datum und Summe das richtige Angebot w&auml;hlen &mdash; es wird
              nichts automatisch ge&ouml;ffnet.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-xs text-gray-600">
                  <th className="py-2 px-3">Angebots-Nr.</th>
                  <th className="py-2 px-3">Kunde</th>
                  <th className="py-2 px-3">Bauvorhaben</th>
                  <th className="py-2 px-3">Datum</th>
                  <th className="py-2 px-3 text-right">Summe</th>
                  <th className="py-2 px-3 text-right">Ablage</th>
                </tr>
              </thead>
              <tbody>
                {(ruecksprungAuswahl ?? []).map((treffer) => (
                  <tr
                    key={treffer.slot}
                    className="border-t hover:bg-blue-50/50 cursor-pointer"
                    onClick={() => {
                      setRuecksprungAuswahl(null);
                      oeffneRuecksprungZiel(treffer.slot);
                    }}
                    data-testid={`ruecksprung-option-${treffer.slot}`}
                  >
                    <td className="py-2 px-3 font-medium whitespace-nowrap">{treffer.offerNumber}</td>
                    <td className="py-2 px-3">{treffer.customerName || '—'}</td>
                    <td className="py-2 px-3">{treffer.projectName || '—'}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{formatDate(treffer.timestamp)}</td>
                    <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">
                      {formatiereSumme(treffer.totalValue) || '—'}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-500">#{treffer.slot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuecksprungAuswahl(null)}>
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offene Änderungen schützen: erst fragen, dann laden (Muster exitDialog). */}
      <Dialog
        open={ruecksprungConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setRuecksprungConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ungespeicherte &Auml;nderungen</DialogTitle>
            <DialogDescription>
              Es ist noch ein Angebot mit ungesicherten &Auml;nderungen ge&ouml;ffnet. Soll es
              gespeichert werden, bevor das Angebot aus Ablage #{ruecksprungConfirm?.slot} aus
              Softec ge&ouml;ffnet wird?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setRuecksprungConfirm(null)} className="sm:order-1">
              Abbrechen
            </Button>
            <Button
              variant="ghost"
              onClick={handleRuecksprungDiscardAndOpen}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 sm:order-2"
            >
              Verwerfen &amp; &ouml;ffnen
            </Button>
            <Button
              onClick={handleRuecksprungSaveAndOpen}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 sm:order-3"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern &amp; &ouml;ffnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Mobile view
  if (isMobile) {
    if (mobileQuoteGateStatus !== null) {
      return (
        <MobileQuoteDesktopNotice
          status={mobileQuoteGateStatus}
          onReturnToOverview={handleReturnFromMobileQuote}
        />
      );
    }

    return (
      <>
        <MobileErrorBoundary>
          <MobileApp onQuoteSnapshotSelect={handleMobileSnapshotSelect} />
        </MobileErrorBoundary>
        {snapshotReviewOverlay}
      </>
    );
  }

  const sidebarWidth = '20rem';
  const previewPaneWidth = 'min(68vw, 1100px)';

  // Data loaded view
  if (isDataLoaded) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header onReturnToUpload={handleBackClick} showNavigation={true} />
        <div className="flex-1 fixed-header-layout overflow-hidden">
          {isGAEBDatabase ? (
            <div className="h-full bg-white p-6 overflow-auto">
              <GAEBEditor />
            </div>
          ) : (
            <div className="h-full min-h-0 relative">
              <div
                className="bg-white flex flex-col h-full min-h-0 transition-[margin] duration-300"
                style={{ marginRight: showPdfPreview ? previewPaneWidth : sidebarWidth }}
              >
                <div className="flex-1 overflow-auto min-h-0">
                  {aufmassView ? <AufmassView /> : kalkulationsView === 'blitz' ? <BlitzkalkulationView /> : <SimpleTable />}
                </div>
                {!aufmassView && kalkulationsView !== 'blitz' && (
                  <div className="flex-shrink-0 border-t border-gray-200">
                    <SimplePagination />
                  </div>
                )}
              </div>
              {showPdfPreview && (
                <div
                  className="fixed right-0 z-30 border-l border-gray-800 bg-gray-950 shadow-2xl"
                  style={{
                    top: 'var(--header-height-current)',
                    bottom: 0,
                    width: previewPaneWidth,
                  }}
                >
                  <div className="h-full p-2">
                    <LivePDFPreview onClose={() => setShowPdfPreview(false)} />
                  </div>
                </div>
              )}
              {!showPdfPreview && (
                <div
                  className="fixed right-0 bg-white border-l border-gray-200 z-20 overflow-hidden"
                  style={{
                    top: 'var(--header-height-current)',
                    bottom: 0,
                    width: sidebarWidth,
                  }}
                >
                  <QuoteSidebar />
                </div>
              )}
              {!showPdfPreview && (
                /* Bleibt "fixed", weil der Umschalter in allen Ansichten
                   (Tabelle/Blitzkalkulation/Aufmaß) erreichbar sein muss —
                   diese haben keine gemeinsame Fußzeile, in die er sich sonst
                   einreihen könnte (das wäre ein größerer Strukturumbau als
                   diese Politur-Runde). Bewusst an die Bildschirmunterkante
                   verankert (bottom-0, Rahmen ohne Unterkante) statt als
                   frei schwebende Pille mit Schlagschatten (bottom-4 +
                   shadow-lg) — liest sich jetzt als angedockter Reiter, nicht
                   als zufällig platziertes Element. */
                <Button
                  onClick={() => setShowPdfPreview(true)}
                  variant="outline"
                  size="sm"
                  className="fixed bottom-0 right-[21rem] z-30 gap-2 bg-white border border-b-0 border-gray-200 rounded-b-none shadow-md"
                >
                  <Eye className="h-4 w-4" />
                  Live Preview
                </Button>
              )}
            </div>
          )}
        </div>

        <Dialog open={exitDialog} onOpenChange={setExitDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
              <DialogDescription>
                Sie haben {quoteItems.length} Position{quoteItems.length !== 1 ? 'en' : ''} im Angebot.
                Möchten Sie Ihre Arbeit speichern bevor Sie fortfahren?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setExitDialog(false)} className="sm:order-1">Abbrechen</Button>
              <Button variant="ghost" onClick={handleDiscardAndExit} className="text-red-600 hover:text-red-700 hover:bg-red-50 sm:order-2">Verwerfen</Button>
              <Button onClick={handleSaveAndExit} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 sm:order-3">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Speichern & Zurück
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {snapshotReviewOverlay}
        {ruecksprungOverlay}
      </div>
    );
  }

  // Dashboard view
  return (
    <div className="min-h-screen bg-gray-50">
      <Header showNavigation={false} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 pt-20">
        {/* Quick Actions: + Neu erstellen */}
        <div className="flex items-center gap-2 mb-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1">
                <Plus className="h-3.5 w-3.5" />
                Neu erstellen
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => initNewQuote()}>
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                Neues Angebot
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/voice-quote-builder')}>
                <Wand2 className="h-4 w-4 mr-2 text-purple-600" />
                KI Builder
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/database-builder')}>
                <PenLine className="h-4 w-4 mr-2" />
                Datenbank erstellen
              </DropdownMenuItem>
              {/* Zwei getrennte Excel-Wege, ehrlich benannt: /excel-import baut eine
                  Preis-DATENBANK aus der Tabelle, /excel-lv-import liest ein LV samt
                  Urkalkulation direkt als Angebots-Entwurf ein. */}
              <DropdownMenuItem onClick={() => navigate('/excel-import')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Excel Import (Preisdatenbank)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/excel-lv-import')}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
                Excel Import (LV + Urkalkulation)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/gaeb-import')}>
                <FileCode className="h-4 w-4 mr-2" />
                X83 Import
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/pdf-lv-import')}>
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                PDF-LV Import
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ifc-import')}>
                <Boxes className="h-4 w-4 mr-2 text-teal-600" />
                IFC / BIM Import
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <PDFImport />
        </div>
        {/* Main Panel */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 space-y-3">
            {/* Top row: Tabs and Search */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <Tabs value={activeTab} onValueChange={handleDashboardTabChange} className="w-full sm:w-auto">
                <TabsList className="bg-gray-100 border border-gray-200">
                  <TabsTrigger
                    value="databases"
                    data-testid="tab-databases"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                  >
                    <DatabaseIcon className="h-4 w-4 mr-1.5" />
                    Datenbanken
                    <Badge className="ml-2 bg-gray-200 text-gray-600 text-xs">{filteredDatabases.length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="quotes"
                    data-testid="tab-quotes"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Entwürfe
                    <Badge className="ml-2 bg-gray-200 text-gray-600 text-xs">{filteredQuotes.length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="outputs"
                    data-testid="tab-exports"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                  >
                    <FileOutput className="h-4 w-4 mr-1.5" />
                    Exporte
                    <Badge className="ml-2 bg-gray-200 text-gray-600 text-xs">{exports.length}</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="relative flex-1 sm:w-64 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={activeTab === 'quotes' ? 'Nummer, Kunde, Bauvorhaben...' : 'Suchen...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Bottom row: Sort, Filter, Limit, View options */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {/* Type filter (only for databases tab) */}
              {activeTab === 'databases' && (
                <div className="flex items-center gap-1.5">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="h-8 w-[110px] bg-gray-50 border-gray-200 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Typen</SelectItem>
                      <SelectItem value="gaeb-import">X83/X84</SelectItem>
                      <SelectItem value="excel-import">Excel</SelectItem>
                      <SelectItem value="voice-created">KI/Sprache</SelectItem>
                      <SelectItem value="manual">Manuell</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sort dropdown */}
              <div className="flex items-center gap-1.5">
                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                  <SelectTrigger className="h-8 w-[150px] bg-gray-50 border-gray-200 text-xs" data-testid="dashboard-sort-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created-desc">Erstellt (neu→alt)</SelectItem>
                    <SelectItem value="created-asc">Erstellt (alt→neu)</SelectItem>
                    <SelectItem value="updated-desc">Geändert (neu→alt)</SelectItem>
                    <SelectItem value="updated-asc">Geändert (alt→neu)</SelectItem>
                    <SelectItem value="name-asc">Name A-Z</SelectItem>
                    <SelectItem value="name-desc">Name Z-A</SelectItem>
                    {activeTab === 'quotes' && (
                      <>
                        <SelectItem value="number-asc">Angebotsnr. ↑</SelectItem>
                        <SelectItem value="number-desc">Angebotsnr. ↓</SelectItem>
                      </>
                    )}
                    <SelectItem value="size-desc">{activeTab === 'quotes' || activeTab === 'outputs' ? 'Wert ↓' : 'Größe ↓'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Limit dropdown */}
              <div className="flex items-center gap-1.5">
                <SlidersHorizontal className="h-4 w-4 text-gray-400" />
                <Select value={String(displayLimit)} onValueChange={(v) => setDisplayLimit(Number(v))}>
                  <SelectTrigger className="h-8 w-[90px] bg-gray-50 border-gray-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="0">Alle</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1" />

              {/* Display count indicator */}
              <span className="text-xs text-gray-500">
                {activeTab === 'databases'
                  ? `${displayedDatabases.length} von ${filteredDatabases.length}`
                  : `${displayedQuotes.length} von ${filteredQuotes.length}`
                }
              </span>

              {/* View mode toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`h-7 w-7 p-0 ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="view-mode-list"
                >
                  <List className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`h-7 w-7 p-0 ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="view-mode-grid"
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[calc(100vh-280px)] overflow-y-auto bg-gray-50/50">
            {loading && (databases.length === 0 || quotes.length === 0) ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-3" />
                <span className="text-gray-500">Lade Daten...</span>
              </div>
            ) : activeTab === 'databases' ? (
              displayedDatabases.length === 0 ? (
                <EmptyState
                  icon={DatabaseIcon}
                  title={filteredDatabases.length === 0 ? "Keine Datenbanken" : "Keine Ergebnisse"}
                  description={filteredDatabases.length === 0
                    ? "Erstellen Sie eine neue Datenbank manuell, per Sprache, Excel oder X83-Import."
                    : "Ändern Sie die Filter oder Suchbegriffe."}
                  action={filteredDatabases.length === 0 ? () => navigate('/database-builder') : undefined}
                  actionLabel={filteredDatabases.length === 0 ? "Manuell erstellen" : undefined}
                />
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {displayedDatabases.map((db) => (
                    <DatabaseCard
                      key={db.id}
                      database={db}
                      onClick={() => handleDatabaseClick(db)}
                      onRename={(e) => {
                        e.stopPropagation();
                        setRenameDialog({ isOpen: true, type: 'database', id: db.id, currentName: db.name });
                        setNewName(db.name);
                      }}
                      onDownload={(format, e) => {
                        e.stopPropagation();
                        handleDownload('database', db.id, format);
                      }}
                      onDuplicate={(e) => handleDuplicateDatabase(db.id, e)}
                      onX83Download={db.type === 'gaeb-import' ? (e) => handleX83Download(db.id, e) : undefined}
                      onDelete={(e) => handleDeleteDatabase(db.id, e)}
                      deleting={deletingId === db.id}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {displayedDatabases.map((db) => (
                    <DatabaseRow
                      key={db.id}
                      database={db}
                      onClick={() => handleDatabaseClick(db)}
                      onRename={(e) => {
                        e.stopPropagation();
                        setRenameDialog({ isOpen: true, type: 'database', id: db.id, currentName: db.name });
                        setNewName(db.name);
                      }}
                      onDownload={(format, e) => {
                        e.stopPropagation();
                        handleDownload('database', db.id, format);
                      }}
                      onDuplicate={(e) => handleDuplicateDatabase(db.id, e)}
                      onX83Download={db.type === 'gaeb-import' ? (e) => handleX83Download(db.id, e) : undefined}
                      onDelete={(e) => handleDeleteDatabase(db.id, e)}
                      deleting={deletingId === db.id}
                    />
                  ))}
                </div>
              )
            ) : activeTab === 'quotes' ? (
              displayedQuotes.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title={filteredQuotes.length === 0 ? "Keine Angebote" : "Keine Ergebnisse"}
                  description={filteredQuotes.length === 0
                    ? "Laden Sie eine Datenbank und erstellen Sie Ihr erstes Angebot."
                    : "Ändern Sie die Filter oder Suchbegriffe."}
                />
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {displayedQuotes.map((quote) => (
                    <QuoteCard
                      key={quote.slot}
                      quote={quote}
                      duplicateNumber={!!quote.offerNumber && duplicateOfferNumbers.has(quote.offerNumber.trim())}
                      onClick={() => handleQuoteSelect(quote.slot)}
                      onRename={(e) => {
                        e.stopPropagation();
                        setRenameDialog({ isOpen: true, type: 'quote', id: quote.slot, currentName: quote.projectName || getQuoteDisplayName(quote, `Angebot ${quote.slot}`) });
                        setNewName(quote.projectName || '');
                      }}
                      onDownload={(format, e) => {
                        e.stopPropagation();
                        handleDownload('quote', quote.slot, format);
                      }}
                      onDuplicate={(e) => handleDuplicateQuote(quote.slot, e)}
                      onDelete={(e) => handleDeleteQuote(quote.slot, e)}
                      deleting={deletingId === quote.slot}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {displayedQuotes.map((quote) => (
                    <QuoteRow
                      key={quote.slot}
                      quote={quote}
                      duplicateNumber={!!quote.offerNumber && duplicateOfferNumbers.has(quote.offerNumber.trim())}
                      onClick={() => handleQuoteSelect(quote.slot)}
                      onRename={(e) => {
                        e.stopPropagation();
                        setRenameDialog({ isOpen: true, type: 'quote', id: quote.slot, currentName: quote.projectName || getQuoteDisplayName(quote, `Angebot ${quote.slot}`) });
                        setNewName(quote.projectName || '');
                      }}
                      onDownload={(format, e) => {
                        e.stopPropagation();
                        handleDownload('quote', quote.slot, format);
                      }}
                      onDuplicate={(e) => handleDuplicateQuote(quote.slot, e)}
                      onDelete={(e) => handleDeleteQuote(quote.slot, e)}
                      deleting={deletingId === quote.slot}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              )
            ) : (
              // Outputs/Dokumente Tab - Shows saved exports and quotes with PDF/GAEB focus
              exports.length === 0 && quotes.length === 0 ? (
                <EmptyState
                  icon={FileOutput}
                  title="Keine Dokumente"
                  description="Exportieren Sie ein Angebot als PDF oder GAEB um es hier zu sehen."
                />
              ) : (
                <div className="space-y-3">
                  {/* Section: Saved Exports */}
                  {exports.length > 0 && (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <FileOutput className="h-4 w-4" />
                        Gespeicherte Exporte ({exports.length})
                      </h3>
                      {exports.map((exp) => {
                        const typeStyles: Record<string, { bg: string; text: string; label: string }> = {
                          'pdf': { bg: 'bg-green-100', text: 'text-green-700', label: 'PDF' },
                          'gaeb-x83': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'X83' },
                          'gaeb-x84': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'X84' },
                          'gaeb-x85': { bg: 'bg-red-100', text: 'text-red-700', label: 'X85' },
                          'excel': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Excel' },
                        };
                        const style = typeStyles[exp.exportType] || typeStyles.pdf;

                        return (
                          <div
                            key={exp.id}
                            className="group bg-white rounded-xl border border-gray-200 hover:border-green-400 hover:shadow-md transition-all overflow-hidden"
                          >
                            <div className="flex items-stretch">
                              {/* Type icon */}
                              <div className={`w-16 ${style.bg} flex items-center justify-center border-r border-gray-100`}>
                                <div className="text-center">
                                  {exp.exportType === 'pdf' ? (
                                    <FileText className={`h-6 w-6 ${style.text} mx-auto`} />
                                  ) : exp.exportType.startsWith('gaeb') ? (
                                    <FileCode className={`h-6 w-6 ${style.text} mx-auto`} />
                                  ) : (
                                    <FileSpreadsheet className={`h-6 w-6 ${style.text} mx-auto`} />
                                  )}
                                  <span className={`text-[9px] ${style.text} font-medium mt-0.5 block`}>
                                    {style.label}
                                  </span>
                                </div>
                              </div>

                              {/* Main content */}
                              <div className="flex-1 p-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <h3 className="font-medium text-gray-900 truncate text-sm">
                                        {exp.sourceName}
                                      </h3>
                                      <Badge variant="secondary" className={`text-[9px] ${style.bg} ${style.text} px-1.5`}>
                                        {style.label}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-gray-500 truncate">
                                      {exp.metadata.customerName || exp.filename}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                      {exp.metadata.totalValue && (
                                        <span className="font-medium text-green-700">
                                          {formatCurrency(exp.metadata.totalValue)}
                                        </span>
                                      )}
                                      <span>{(exp.fileSize / 1024).toFixed(0)} KB</span>
                                      <span>{formatDate(exp.createdAt)}</span>
                                    </div>
                                  </div>
                                  {/* Actions */}
                                  <div className="flex items-center gap-1">
                                    {exp.exportType === 'pdf' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => window.open(`/api/exports/${exp.id}/preview`, '_blank')}
                                        className="h-7 w-7 p-0"
                                        title="Vorschau"
                                      >
                                        <Eye className="h-4 w-4 text-gray-500" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => window.open(`/api/exports/${exp.id}/download`, '_blank')}
                                      className="h-7 w-7 p-0"
                                      title="Herunterladen"
                                    >
                                      <Download className="h-4 w-4 text-gray-500" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setDeleteExportTarget(exp.id)}
                                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                                      title="Löschen"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Info box */}
                  <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                    <div className="flex items-start gap-2">
                      <FileOutput className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-800 text-sm">Dokument-Management</h4>
                        <p className="text-xs text-green-700 mt-0.5">
                          Vorschau generiert PDFs on-demand. Exportierte GAEB-Dateien (X84, X85) werden gespeichert.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Load more button */}
            {displayLimit > 0 && (
              (activeTab === 'databases' && displayedDatabases.length < filteredDatabases.length) ||
              (activeTab === 'quotes' && displayedQuotes.length < filteredQuotes.length) ||
              (activeTab === 'outputs' && displayedQuotes.length < filteredQuotes.length)
            ) && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDisplayLimit(prev => prev + 25)}
                  className="text-gray-600"
                >
                  Mehr laden ({activeTab === 'databases'
                    ? filteredDatabases.length - displayedDatabases.length
                    : filteredQuotes.length - displayedQuotes.length
                  } weitere)
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Rename Dialog */}
      <Dialog open={renameDialog?.isOpen || false} onOpenChange={(open) => !open && setRenameDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {renameDialog?.type === 'database' ? 'Datenbank umbenennen' : 'Angebotstitel umbenennen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-name">{renameDialog?.type === 'database' ? 'Neuer Name' : 'Bauvorhaben / Titel'}</Label>
              <Input
                id="new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={renameDialog?.type === 'database' ? 'Name eingeben...' : 'z.B. Sanierung Musterstraße'}
                className="mt-1"
                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleRename} disabled={isRenaming || !newName.trim()}>
              {isRenaming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Umbenennen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Database Action Dialog - choose between edit DB or create offer */}
      <Dialog open={dbActionDialog?.open ?? false} onOpenChange={() => setDbActionDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dbActionDialog?.database.type === 'gaeb-import' && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">X83</Badge>
              )}
              {dbActionDialog?.database.name}
            </DialogTitle>
            <DialogDescription>
              {dbActionDialog?.database.type === 'gaeb-import'
                ? 'Was möchten Sie mit dieser X83-Ausschreibung tun?'
                : 'Was möchten Sie mit dieser Datenbank tun?'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            {dbActionDialog?.database.type === 'gaeb-import' ? (
              <>
                {/* GAEB-specific options */}
                <Button
                  variant="outline"
                  className="justify-start h-auto py-3 px-4 border-orange-200 hover:bg-orange-50"
                  onClick={() => dbActionDialog && handleEditGAEB(dbActionDialog.database)}
                >
                  <FileCode className="mr-3 h-5 w-5 text-orange-600" />
                  <div className="text-left">
                    <div className="font-medium">X83 bearbeiten → Angebot exportieren</div>
                    <div className="text-xs text-gray-500">Preise eingeben, X84/X85 exportieren</div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="justify-start h-auto py-3 px-4"
                  onClick={() => dbActionDialog && handleCreateDbFromGAEB(dbActionDialog.database)}
                >
                  <DatabaseIcon className="mr-3 h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-medium">Datenbank erstellen aus X83</div>
                    <div className="text-xs text-gray-500">X83-Positionen als normale Datenbank speichern</div>
                  </div>
                </Button>
                <Button
                  className="justify-start h-auto py-3 px-4 bg-orange-600 hover:bg-orange-700"
                  onClick={() => dbActionDialog && handleCreateOfferFromDb(dbActionDialog.database)}
                >
                  <Plus className="mr-3 h-5 w-5" />
                  <div className="text-left">
                    <div className="font-medium">Neues Angebot erstellen</div>
                    <div className="text-xs text-orange-100">Positionen auswählen und Angebot generieren</div>
                  </div>
                </Button>
              </>
            ) : (
              <>
                {/* Regular database options */}
                <Button
                  variant="outline"
                  className="justify-start h-auto py-3 px-4"
                  onClick={() => dbActionDialog && handleEditDatabase(dbActionDialog.database)}
                >
                  <Edit className="mr-3 h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-medium">Datenbank bearbeiten</div>
                    <div className="text-xs text-gray-500">Artikel hinzufügen, ändern oder löschen</div>
                  </div>
                </Button>
                <Button
                  className="justify-start h-auto py-3 px-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => dbActionDialog && handleCreateOfferFromDb(dbActionDialog.database)}
                >
                  <Plus className="mr-3 h-5 w-5" />
                  <div className="text-left">
                    <div className="font-medium">Neues Angebot erstellen</div>
                    <div className="text-xs text-blue-100">Positionen auswählen und Angebot generieren</div>
                  </div>
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {snapshotReviewOverlay}
      {ruecksprungOverlay}

      {/* Document PDF Preview Modal */}
      <PDFViewerModal
        isOpen={documentPreview.isOpen}
        onClose={closeDocumentPreview}
        pdfBlob={documentPreview.pdfBlob}
        fileName={documentPreview.fileName}
        metadata={documentPreview.metadata}
      />

      {/* Loading overlay for document preview */}
      {documentPreview.isOpen && documentPreview.isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg p-6 flex items-center gap-4 shadow-xl">
            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
            <div>
              <p className="font-medium">PDF wird generiert...</p>
              <p className="text-sm text-gray-500">{documentPreview.metadata.offerNumber}</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Quote Confirmation */}
      <AlertDialog open={!!deleteQuoteTarget} onOpenChange={(open) => !open && setDeleteQuoteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Angebot löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie dieses Angebot wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteQuote} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Database Confirmation */}
      <AlertDialog open={!!deleteDatabaseTarget} onOpenChange={(open) => !open && setDeleteDatabaseTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Datenbank löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Datenbank wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDatabase} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Export Confirmation */}
      <AlertDialog open={!!deleteExportTarget} onOpenChange={(open) => !open && setDeleteExportTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Export wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteExport} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Compact Components
function EmptyState({ icon: Icon, title, description, action, actionLabel }: { 
  icon: any; title: string; description: string; action?: () => void; actionLabel?: string 
}) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-500 text-sm mb-4">{description}</p>
      {action && actionLabel && (
        <Button onClick={action} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1.5" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

// Color coding for database sources - Professional Fortuna Softec style
const sourceStyles: Record<string, { bg: string; border: string; icon: string; badge: string; badgeText: string }> = {
  'voice-created': {
    bg: 'bg-gradient-to-br from-violet-50 to-purple-50',
    border: 'border-violet-200 hover:border-violet-400 hover:shadow-md',
    icon: 'bg-violet-100 text-violet-600',
    badge: 'bg-violet-100 text-violet-700 border-violet-200',
    badgeText: 'KI'
  },
  'excel-import': {
    bg: 'bg-gradient-to-br from-emerald-50 to-green-50',
    border: 'border-emerald-200 hover:border-emerald-400 hover:shadow-md',
    icon: 'bg-emerald-100 text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    badgeText: 'Excel'
  },
  'gaeb': {
    bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
    border: 'border-amber-200 hover:border-amber-400 hover:shadow-md',
    icon: 'bg-amber-100 text-amber-600',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    badgeText: 'X83'
  },
  'gaeb-import': {
    bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
    border: 'border-amber-200 hover:border-amber-400 hover:shadow-md',
    icon: 'bg-amber-100 text-amber-600',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    badgeText: 'X83'
  },
  'manual': {
    bg: 'bg-gradient-to-br from-sky-50 to-blue-50',
    border: 'border-sky-200 hover:border-sky-400 hover:shadow-md',
    icon: 'bg-sky-100 text-sky-600',
    badge: 'bg-sky-100 text-sky-700 border-sky-200',
    badgeText: 'Manuell'
  },
};

const defaultStyle = {
  bg: 'bg-white',
  border: 'border-gray-200 hover:border-blue-400 hover:shadow-md',
  icon: 'bg-gray-100 text-gray-500',
  badge: 'bg-gray-100 text-gray-600 border-gray-200',
  badgeText: 'Datenbank'
};

function DatabaseCard({
  database,
  onClick,
  onRename,
  onDownload,
  onDuplicate,
  onX83Download,
  onDelete,
  deleting
}: {
  database: DatabaseMetadata;
  onClick: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDownload: (format: 'json' | 'excel', e: React.MouseEvent) => void;
  onDuplicate: (e: React.MouseEvent) => void;
  onX83Download?: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  deleting: boolean;
}) {
  const style = sourceStyles[database.type || ''] || defaultStyle;
  const snapshot = isQuoteSnapshot(database);

  return (
    <div
      data-testid={`card-database-${database.id}`}
      onClick={onClick}
      className={`${style.bg} hover:shadow-md border ${style.border} rounded-xl p-4 cursor-pointer transition-all group relative`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 ${style.icon} rounded-lg flex items-center justify-center`}>
          <DatabaseIcon className="h-5 w-5" />
        </div>
        <div className="flex items-center gap-2">
          {snapshot ? (
            <Badge
              variant="outline"
              className="text-xs border-amber-300 bg-amber-50 text-amber-800"
              data-testid={`badge-database-snapshot-${database.id}`}
            >
              Angebots-Snapshot
            </Badge>
          ) : (
            <Badge variant="outline" className={`text-xs ${style.badge}`}>
              {style.badgeText}
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-all"
                data-testid={`button-actions-database-${database.id}`}
                aria-label={`Aktionen für Datenbank ${database.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!snapshot && (
                <>
                  <DropdownMenuItem onClick={onRename}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Umbenennen
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onDuplicate}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplizieren
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={(e) => onDownload('json', e as any)}>
                <FileJson className="h-4 w-4 mr-2" />
                Als JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => onDownload('excel', e as any)}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Als Excel
              </DropdownMenuItem>
              {database.type === 'gaeb-import' && onX83Download && (
                <DropdownMenuItem onClick={onX83Download}>
                  <FileCode className="h-4 w-4 mr-2" />
                  Original X83
                </DropdownMenuItem>
              )}
              {!snapshot && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onDelete} className="text-red-600" disabled={deleting}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <h3 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">{database.name}</h3>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">{database.itemCount || 0} Positionen</p>
        <div className="text-[10px] text-gray-400">
          {database.createdAt && new Date(database.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
          {database.updatedAt && database.updatedAt !== database.createdAt && (
            <span className="text-blue-500 ml-1" title="Geändert">↻</span>
          )}
        </div>
      </div>
    </div>
  );
}

function DatabaseRow({
  database,
  onClick,
  onRename,
  onDownload,
  onDuplicate,
  onX83Download,
  onDelete,
  deleting
}: {
  database: DatabaseMetadata;
  onClick: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDownload: (format: 'json' | 'excel', e: React.MouseEvent) => void;
  onDuplicate: (e: React.MouseEvent) => void;
  onX83Download?: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  deleting: boolean;
}) {
  const style = sourceStyles[database.type || ''] || defaultStyle;
  const snapshot = isQuoteSnapshot(database);

  const formatShortDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div
      data-testid={`card-database-${database.id}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 ${style.bg} hover:shadow-sm border ${style.border} rounded-lg cursor-pointer transition-all group`}
    >
      <div className={`w-8 h-8 ${style.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
        <DatabaseIcon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 truncate text-sm group-hover:text-blue-600">{database.name}</h3>
      </div>
      {snapshot ? (
        <Badge
          variant="outline"
          className="text-xs border-amber-300 bg-amber-50 text-amber-800 hidden sm:flex"
          data-testid={`badge-database-snapshot-${database.id}`}
        >
          Angebots-Snapshot
        </Badge>
      ) : (
        <Badge variant="outline" className={`text-xs ${style.badge} hidden sm:flex`}>
          {style.badgeText}
        </Badge>
      )}
      <span className="text-xs text-gray-500 flex-shrink-0">{database.itemCount || 0} Pos.</span>
      <div className="hidden lg:flex flex-col items-end text-[10px] text-gray-400 flex-shrink-0 w-20">
        <span title="Erstellt">{formatShortDate(database.createdAt)}</span>
        {database.updatedAt !== database.createdAt && (
          <span className="text-blue-500" title="Geändert">↻ {formatShortDate(database.updatedAt)}</span>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            data-testid={`button-actions-database-${database.id}`}
            aria-label={`Aktionen für Datenbank ${database.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!snapshot && (
            <>
              <DropdownMenuItem onClick={onRename}>
                <Pencil className="h-4 w-4 mr-2" />
                Umbenennen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplizieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={(e) => onDownload('json', e as any)}>
            <FileJson className="h-4 w-4 mr-2" />
            Als JSON
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => onDownload('excel', e as any)}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Als Excel
          </DropdownMenuItem>
          {database.type === 'gaeb-import' && onX83Download && (
            <DropdownMenuItem onClick={onX83Download}>
              <FileCode className="h-4 w-4 mr-2" />
              Original X83
            </DropdownMenuItem>
          )}
          {!snapshot && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-red-600" disabled={deleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                Löschen
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function QuoteCard({
  quote,
  duplicateNumber,
  onClick,
  onRename,
  onDownload,
  onDuplicate,
  onDelete,
  deleting,
  formatDate,
  formatCurrency
}: {
  quote: QuoteSummary;
  duplicateNumber?: boolean;
  onClick: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDownload: (format: 'json' | 'excel', e: React.MouseEvent) => void;
  onDuplicate: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  deleting: boolean;
  formatDate: (t?: string) => string;
  formatCurrency: (v?: number) => string;
}) {
  // Nummer als eigenes, immer sichtbares Element — sie verschwindet nicht mehr,
  // wenn sie dem Titel gleicht (Ollis „ich finde nirgendwo die Angebotsnummer").
  const { title: quoteTitle, offerNumber, metaParts } = getQuoteListPresentation(
    quote,
    `Angebot ${quote.slot}`,
  );

  return (
    <div
      data-testid={`card-quote-${quote.slot}`}
      onClick={onClick}
      className="bg-white hover:shadow-md border border-gray-200 hover:border-orange-400 rounded-xl p-4 cursor-pointer transition-all group relative"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-all"
            aria-label={`Aktionen für Entwurf #${quote.slot}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onRename}>
            <Pencil className="h-4 w-4 mr-2" />
            Umbenennen
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onDuplicate}
            data-testid={`button-duplicate-quote-${quote.slot}`}
          >
            <Copy className="h-4 w-4 mr-2" />
            Duplizieren
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={(e) => onDownload('json', e as any)}>
            <FileJson className="h-4 w-4 mr-2" />
            Als JSON
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => onDownload('excel', e as any)}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Als Excel
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-red-600" disabled={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />
            Löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <FileText className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          {offerNumber && (
            <p
              className="text-xs font-mono font-semibold text-orange-700 truncate"
              data-testid={`text-offer-number-${quote.slot}`}
            >
              {offerNumber}
            </p>
          )}
          <h3 className="font-medium text-gray-900 truncate group-hover:text-orange-600">{quoteTitle}</h3>
          {metaParts.length > 0 && (
            <p className="text-xs text-gray-500 truncate">{metaParts.join(' · ')}</p>
          )}
          {duplicateNumber && (
            <Badge
              variant="outline"
              className="mt-1 bg-amber-50 border-amber-300 text-amber-800"
              title={`Die Angebotsnummer "${quote.offerNumber}" ist mehreren Angeboten zugeordnet.`}
              data-testid={`badge-duplicate-number-${quote.slot}`}
            >
              Nummer mehrfach vergeben
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{quote.itemCount || 0} Pos.</span>
        <span className="text-green-600 font-medium">{formatCurrency(quote.totalValue)}</span>
      </div>
      <p className="text-xs text-gray-400 mt-2">{formatDate(quote.timestamp)}</p>
    </div>
  );
}

function QuoteRow({
  quote,
  duplicateNumber,
  onClick,
  onRename,
  onDownload,
  onDuplicate,
  onDelete,
  deleting,
  formatDate,
  formatCurrency
}: {
  quote: QuoteSummary;
  duplicateNumber?: boolean;
  onClick: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDownload: (format: 'json' | 'excel', e: React.MouseEvent) => void;
  onDuplicate: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  deleting: boolean;
  formatDate: (t?: string) => string;
  formatCurrency: (v?: number) => string;
}) {
  // Nummer als eigenes, immer sichtbares Element — sie verschwindet nicht mehr,
  // wenn sie dem Titel gleicht (Ollis „ich finde nirgendwo die Angebotsnummer").
  const { title: quoteTitle, offerNumber, metaParts } = getQuoteListPresentation(
    quote,
    `Angebot ${quote.slot}`,
  );

  return (
    <div
      data-testid={`row-quote-${quote.slot}`}
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2.5 bg-white hover:shadow-sm border border-gray-200 hover:border-orange-400 rounded-lg cursor-pointer transition-all group"
    >
      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <FileText className="h-4 w-4 text-orange-600" />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <Badge className="bg-orange-100 text-orange-700 border border-orange-200 flex-shrink-0">#{quote.slot}</Badge>
        {offerNumber && (
          <Badge
            variant="outline"
            className="bg-orange-50 border-orange-200 text-orange-700 font-mono flex-shrink-0"
            data-testid={`text-offer-number-${quote.slot}`}
          >
            {offerNumber}
          </Badge>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-900 truncate text-sm group-hover:text-orange-600">
            {quoteTitle}
          </h3>
          {metaParts.length > 0 && (
            <p className="text-xs text-gray-500 truncate">{metaParts.join(' · ')}</p>
          )}
        </div>
        {duplicateNumber && (
          <Badge
            variant="outline"
            className="bg-amber-50 border-amber-300 text-amber-800 flex-shrink-0"
            title={`Die Angebotsnummer "${quote.offerNumber}" ist mehreren Angeboten zugeordnet.`}
            data-testid={`badge-duplicate-number-${quote.slot}`}
          >
            Nummer mehrfach vergeben
          </Badge>
        )}
      </div>
      <span className="text-xs text-gray-500 flex-shrink-0 hidden sm:block">{quote.itemCount || 0} Pos.</span>
      <span className="text-xs text-green-600 font-medium flex-shrink-0">{formatCurrency(quote.totalValue)}</span>
      <span className="text-xs text-gray-400 flex-shrink-0 hidden md:block w-24 text-right">{formatDate(quote.timestamp)}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            aria-label={`Aktionen für Entwurf #${quote.slot}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onRename}>
            <Pencil className="h-4 w-4 mr-2" />
            Umbenennen
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onDuplicate}
            data-testid={`button-duplicate-quote-${quote.slot}`}
          >
            <Copy className="h-4 w-4 mr-2" />
            Duplizieren
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={(e) => onDownload('json', e as any)}>
            <FileJson className="h-4 w-4 mr-2" />
            Als JSON
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => onDownload('excel', e as any)}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Als Excel
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-red-600" disabled={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />
            Löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
